import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Agentous';
const DEFAULT_IMAGE = '/og-image.png';
const DEFAULT_IMAGE_HEIGHT = '907';
const DEFAULT_IMAGE_WIDTH = '1733';

const DEFAULT_SITE_URL = 'https://agentous.ai';
const SITE_URL = (
  import.meta.env.VITE_CLIENT_BASE_URL ||
  import.meta.env.VITE_CLIENT_BASE_URL_DEV ||
  DEFAULT_SITE_URL
).replace(/\/+$/, '');

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

interface PageSeoProps {
  title: string;
  description: string;
  path?: string;
  includeSiteName?: boolean;
  image?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  tags?: readonly string[];
  jsonLd?: JsonLd;
}

export function getAbsoluteUrl(value = '/'): string {
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalized = value.startsWith('/') ? value : `/${value}`;
  return `${SITE_URL}${normalized}`;
}

function formatTitle(title: string, includeSiteName: boolean): string {
  const normalizedTitle = title.replace(/\s+[—–-]\s+/g, ' | ');

  return !includeSiteName || normalizedTitle.toLowerCase().includes('agentous')
    ? normalizedTitle
    : `${normalizedTitle} | ${SITE_NAME}`;
}

function formatPathSegment(segment: string): string {
  return segment
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getBreadcrumbJsonLd(path: string, pageTitle: string): JsonLd | null {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  let currentPath = '';
  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: SITE_NAME,
      item: getAbsoluteUrl('/'),
    },
    ...segments.map((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;

      return {
        '@type': 'ListItem',
        position: index + 2,
        name: isLast ? pageTitle : formatPathSegment(segment),
        item: getAbsoluteUrl(currentPath),
      };
    }),
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

export default function PageSeo({
  title,
  description,
  path = '/',
  includeSiteName = true,
  image = DEFAULT_IMAGE,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  tags = [],
  jsonLd,
}: PageSeoProps) {
  const pageTitle = formatTitle(title, includeSiteName);
  const canonicalUrl = getAbsoluteUrl(path);
  const imageUrl = getAbsoluteUrl(image);
  const usesDefaultImage = image === DEFAULT_IMAGE;
  const breadcrumbJsonLd = getBreadcrumbJsonLd(path, pageTitle);
  const jsonLdItems = [
    ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
    ...(breadcrumbJsonLd ? [breadcrumbJsonLd] : []),
  ];

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      {usesDefaultImage && (
        <>
          <meta property="og:image:width" content={DEFAULT_IMAGE_WIDTH} />
          <meta property="og:image:height" content={DEFAULT_IMAGE_HEIGHT} />
        </>
      )}
      <meta property="og:image:alt" content={`${SITE_NAME} preview image`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} preview image`} />

      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {author && <meta property="article:author" content={author} />}
      {tags.map((tag, index) => (
        <meta key={`${tag}-${index}`} property="article:tag" content={tag} />
      ))}

      {jsonLdItems.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
